// src/Transcribe.js
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { MicrophoneIcon } from "@heroicons/react/20/solid";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const StreamingPoint = process.env.REACT_APP_API_STREAMING;

class Transcribe extends React.Component {
  state = {
    isRecording: false,
    status: "idle",
    sessionId: uuidv4(),
    transcripts: [],
    currentMediaRecorder: null,
    showPopup: false,
    showTooltip: false,
    livePreview: "",
  };

  static defaultProps = {
    onInputUpdate: () => {},
  };

  stream = null;
  mediaRecorder = null;
  ws = null;
  sentToParentOnce = false;
  recordingEnded = false;
  finalTranscriptCache = "";

  componentWillUnmount() {
    this.stop(false);
  }

  getWebSocketUrl() {
    if (StreamingPoint) return StreamingPoint;

    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    return isLocal
      ? "ws://localhost:4000/transcribe"
      : `wss://${window.location.host}/transcribe`;
  }

  start = async () => {
    if (this.state.isRecording) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setState({ status: "getUserMedia not supported" });
      toast.error("Microphone not supported in this browser.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.stream = stream;

      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        toast.info("Recording started…", { autoClose: 1000 });
        this.sentToParentOnce = false;
        this.recordingEnded = false;
        this.finalTranscriptCache = "";

        this.setState(
          {
            isRecording: true,
            status: "recording",
            livePreview: "",
            showPopup: true,
          },
          () => {
            this.startRecorder();
          }
        );
      };

      this.ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          if (msg.type === "transcript") {
            const { text, isFinal } = msg;

            if (!isFinal) {
              this.setState({ livePreview: text });
              return;
            }

            this.setState((prev) => ({
              livePreview: "",
              transcripts: [
                ...prev.transcripts,
                {
                  sequence: prev.transcripts.length + 1,
                  text,
                  jobName: "streaming",
                },
              ],
            }));
          } else if (msg.type === "info") {
            console.info("Transcribe WS info:", msg.message);
          } else if (msg.type === "warning") {
            console.warn("Transcribe WS warning:", msg.message);
          } else if (msg.type === "error") {
            console.error("Transcribe WS error:", msg.message);
            this.setState({ status: "transcribe error" });
            toast.error("Transcription error.");
          }
        } catch (e) {
          console.error("WS parse error:", e);
        }
      };

      this.ws.onerror = () => {
        this.setState({ status: "WebSocket error" });
        toast.error("WebSocket connection error.");
      };

      this.ws.onclose = () => {
        this.recordingEnded = true;
        this.finalTranscriptCache = this.combinedTranscript();

        if (!this.state.showPopup) {
          this.sendFinalToParentOnce();
        }

        this.ws = null;
      };
    } catch (err) {
      this.setState({ status: "mic permission denied or unsupported" });
      toast.error("Microphone permission denied.");
    }
  };

  startRecorder = () => {
    if (!this.stream || !this.state.isRecording) return;

    try {
      this.mediaRecorder = new MediaRecorder(this.stream);
    } catch (e) {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
      return;
    }

    this.setState({ currentMediaRecorder: this.mediaRecorder });

    this.mediaRecorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      e.data.arrayBuffer().then((buf) => {
        try {
          this.ws.send(buf);
        } catch (err) {
          console.error("WS send error:", err);
        }
      });
    };

    this.mediaRecorder.onerror = () => {
      this.setState({ status: "MediaRecorder error" });
      toast.error("MediaRecorder error.");
    };

    this.mediaRecorder.onstop = () => {
      this.setState({ currentMediaRecorder: null });
    };

    this.mediaRecorder.start(250);
  };

  stop = (showPopup = true) => {
    if (!this.state.isRecording && !this.stream && !this.ws) return;

    this.setState({
      isRecording: false,
      status: "stopped",
      showPopup: showPopup ? true : this.state.showPopup,
    });

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: "end" }));
      } catch (e) {
        console.error("WS send end error:", e);
      }

      setTimeout(() => {
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }
      }, 500);
    } else if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    toast.info("Recording stopped.", { autoClose: 1000 });
  };

  sendFinalToParentOnce = () => {
    if (this.sentToParentOnce) return;

    const full = (this.finalTranscriptCache || this.combinedTranscript()).trim();
    if (!full) return;

    this.sentToParentOnce = true;
    this.props.onInputUpdate({ transcription: full, output: null });
  };

  toggleRecording = () => {
    if (this.state.isRecording) {
      this.stop(true);
    } else {
      this.start();
    }
  };

  combinedTranscript() {
    const { transcripts } = this.state;
    return transcripts
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((t) => t.text)
      .join(" ")
      .trim();
  }

  renderPopup() {
    const { showPopup, isRecording, currentMediaRecorder, livePreview, transcripts } =
      this.state;
    if (!showPopup) return null;

    const transcriptText = (
      this.combinedTranscript() + (livePreview ? " " + livePreview : "")
    ).trim();

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm px-4">
        <div className="w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5 text-white">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-bold tracking-tight">
                  Live Transcription
                </h3>
                <p className="mt-1 text-sm text-white/80">
                  Record your consultation and review the transcript as it appears.
                </p>
              </div>

              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                  isRecording
                    ? "bg-red-500/20 text-white"
                    : "bg-white/15 text-white"
                }`}
              >
                {isRecording ? "Recording" : "Stopped"}
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Top controls */}
            <div className="mb-5 rounded-2xl border border-sky-100 bg-sky-50/70 p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    className={`flex items-center rounded-2xl px-4 py-3 text-white shadow-md transition ${
                      isRecording
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-sky-800 hover:bg-sky-900"
                    }`}
                    onClick={this.toggleRecording}
                  >
                    <MicrophoneIcon className="h-6 w-6" />
                    <span className="ml-2 text-sm font-semibold">
                      {isRecording ? "Recording" : "Start Recording"}
                    </span>
                  </button>

                  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                    <div style={{ width: 220, height: 42 }}>
                      {isRecording && currentMediaRecorder ? (
                        <LiveAudioVisualizer
                          mediaRecorder={currentMediaRecorder}
                          width={220}
                          height={42}
                          barWidth={3}
                          gap={2}
                          barColor={"#205680"}
                        />
                      ) : (
                        <div className="flex h-[42px] items-center text-xs text-slate-400">
                          Waiting for audio...
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (this.state.isRecording) {
                      this.stop(false);
                    }

                    this.setState({ showPopup: false }, () => {
                      if (this.recordingEnded) {
                        this.sendFinalToParentOnce();
                      }
                    });
                  }}
                  className="rounded-2xl bg-gradient-to-r from-sky-700 to-cyan-700 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:from-sky-800 hover:to-cyan-800"
                >
                  Finish
                </button>
              </div>
            </div>

            {/* Status chips */}
            <div className="mb-4 flex flex-wrap gap-2">
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                Status: {this.state.status}
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                Segments: {transcripts.length}
              </div>
              {livePreview && (
                <div className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                  Live preview active
                </div>
              )}
            </div>

            {/* Transcript area */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-sm font-semibold text-slate-800">
                  Transcript
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Text appears progressively while you speak.
                </div>
              </div>

              <div className="p-4">
                <textarea
                  className="min-h-[520px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-800 outline-none"
                  readOnly
                  value={transcriptText}
                  placeholder="Transcript will appear here as you speak…"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isRecording, showTooltip } = this.state;

    return (
      <>
        <div className="flex items-center justify-center">
          <div className="relative flex items-center">
            <button
              id="microphone"
              className={`group relative flex items-center rounded-2xl px-3 py-3 text-white shadow-md transition ${
                isRecording
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-sky-800 hover:bg-sky-900"
              }`}
              onClick={this.toggleRecording}
              onMouseEnter={() => this.setState({ showTooltip: true })}
              onMouseLeave={() => this.setState({ showTooltip: false })}
            >
              <MicrophoneIcon className="h-8 w-8" />
              {isRecording && (
                <span className="ml-2 text-sm font-semibold">Recording</span>
              )}
            </button>

            {showTooltip && (
              <div className="absolute left-1/2 top-[-48px] z-10 -translate-x-1/2 whitespace-nowrap rounded-xl bg-slate-800 px-3 py-2 text-xs text-white shadow-lg">
                Voice recording
              </div>
            )}
          </div>
        </div>

        {this.renderPopup()}
        <ToastContainer />
      </>
    );
  }
}

export default Transcribe;